import { useEffect, useRef } from 'react';
import './App.css';
import { LicenseInfo } from '@mui/x-license';
import { createPath, useLocation } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import AppRoutes from './components/AppRoutes';
import { SideNavBar } from './components/Compounds/Navigation/SideNavBar';
import { getUserErrorMessageSubject, useErrorStore } from './store/Error';
import { DetailsDrawer } from './components/Compounds/DetailsDrawer/DetailsDrawer';
import { clearDrawerState, getFromDrawerSubject, updateFromDrawerSubject } from './store/Drawer';
import { apiService } from './Api/APIService';
import { useAuth } from './Context/AuthContext';
import useNavigatedFromDetailToList from './utils/useNavFromDetailToList';
import { NavBar } from './components/Compounds/Navigation/NavBar';
import { useFeatureFlags } from './Api/useFeatureFlags';
import { ErrorBoundary } from './components/Atoms/ErrorBoundary';
import { setSignInUnavailable } from './store/Auth';

export const App = () => {
  const location = useLocation();
  const { signIn, authState } = useAuth();
  // Read by a subscription made once (the error store replays its last value, so resubscribing would re-fire a 401).
  const latest = useRef({ location, signIn, isAuthenticated: authState?.isAuthenticated });
  useEffect(() => {
    latest.current = { location, signIn, isAuthenticated: authState?.isAuthenticated };
  });
  const wentFromDetailToList = useNavigatedFromDetailToList();
  // Init general feature flags.
  useFeatureFlags();
  useErrorStore();

  useEffect(() => {
    LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_V9 || '');

    const errorSubscription = getUserErrorMessageSubject().subscribe((errors) => {
      if (!errors) return;
      const { location: current, signIn: startSignIn, isAuthenticated } = latest.current;
      if (errors.includes('401') || (errors.includes('expired') && !isAuthenticated)) {
        try {
          apiService.clearToken();
          // The redirect is a promise: the surrounding try/catch cannot see its rejection.
          startSignIn(createPath(current)).catch((error) => {
            datadogLogs.logger.error('Sign-in redirect failed', { error });
            setSignInUnavailable(true);
          });
        } catch (err) {
          datadogLogs.logger.error('Error', { err });
        }
      }
    });

    return () => {
      if (errorSubscription) errorSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!getFromDrawerSubject().getValue()) {
      clearDrawerState();
      updateFromDrawerSubject(false);
    }
  }, [wentFromDetailToList]);

  return (
    <div
      data-testid="app-container"
      className="flex">
      <SideNavBar />
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-20">
          <NavBar />
        </header>
        <main className="flex flex-1 flex-col relative overflow-y-auto">
          <ErrorBoundary scope="app-root">
            <AppRoutes />
          </ErrorBoundary>
        </main>
      </div>
      <DetailsDrawer />
    </div>
  );
};
